import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';

import { Typography } from '@mui/material';
import LockClockOutlinedIcon from '@mui/icons-material/LockClockOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';

import './styles.scss';

const STATUS = {
  LOADING: 'loading',
  PLAYING: 'playing',
  EXPIRED: 'expired',
  MISSING: 'missing',
};

const DemoWatchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [status, setStatus] = useState(STATUS.LOADING);
  const [video, setVideo] = useState(null); // { src, title }
  const capturedRef = useRef(false);

  // re-verification returns to whichever demo entry point this came from
  const verifyPath = pathname.includes('certify') ? '/certify-demo' : '/demo';

  useEffect(() => {
    // capture once — the link is consumed on first load, never persisted
    if (capturedRef.current) return;
    capturedRef.current = true;

    const src = searchParams.get('src');
    const title = searchParams.get('t') || 'Platform demonstration';

    if (src) {
      setVideo({ src, title });
      setStatus(STATUS.PLAYING);
      // strip the signed URL out of the address bar + browser history
      setSearchParams({}, { replace: true });
    } else {
      // refresh, or opened without a link
      setStatus(STATUS.MISSING);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goVerify = () => navigate(verifyPath);

  return (
    <div className="demo-watch">
      <div className="demo-watch__container">
        <header className="demo-watch__mast">
          <p className="demo-watch__eyebrow">Lusid Certify · Platform Demonstration</p>
          {status === STATUS.PLAYING && video && (
            <h1 className="demo-watch__title">{video.title}</h1>
          )}
        </header>

        {/* ---------- Playing ---------- */}
        {status === STATUS.PLAYING && video && (
          <div className="demo-watch__stage">
            <div className="demo-watch__frame">
              <video
                className="demo-watch__video"
                src={video.src}
                controls
                controlsList="nodownload"
                onContextMenu={(e) => e.preventDefault()}
                onError={() => setStatus(STATUS.EXPIRED)}
                preload="metadata"
                playsInline
              />
            </div>
            <p className="demo-watch__hint">
              This link is unique to you and expires 10 minutes after it was sent. For
              security it isn&apos;t kept once this page loads — if you refresh, you&apos;ll
              need to verify again.
            </p>
          </div>
        )}

        {/* ---------- Expired ---------- */}
        {status === STATUS.EXPIRED && (
          <div className="demo-watch__panel">
            <LockClockOutlinedIcon className="demo-watch__panel-icon" />
            <Typography variant="h4" className="demo-watch__panel-title">
              This link has expired
            </Typography>
            <p className="demo-watch__panel-text">
              Demonstration links are valid for 10 minutes. Verify again to get a fresh
              set of links sent to your inbox.
            </p>
            <button className="demo-watch__btn" onClick={goVerify}>
              <ReplayOutlinedIcon fontSize="small" />
              Verify again
            </button>
          </div>
        )}

        {/* ---------- Missing (refresh / direct visit) ---------- */}
        {status === STATUS.MISSING && (
          <div className="demo-watch__panel">
            <LockClockOutlinedIcon className="demo-watch__panel-icon" />
            <Typography variant="h4" className="demo-watch__panel-title">
              Nothing to play here
            </Typography>
            <p className="demo-watch__panel-text">
              For security, the video link isn&apos;t kept once the page loads — so a
              refresh or a direct visit won&apos;t replay it. Open the link from your email
              again, or if it has expired, verify once more to get new links.
            </p>
            <button className="demo-watch__btn" onClick={goVerify}>
              <ReplayOutlinedIcon fontSize="small" />
              Verify again
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemoWatchPage;
